import { APPOINTMENT_ONBOARDING_STEP_TARGETS } from './onboardingAppointmentStepTargets';
import { SERVICE_ASSIGNMENT_ONBOARDING_STEP_TARGETS } from './onboardingServiceAssignmentStepTargets';

export interface OnboardingStepTarget {
  id: string;
  hint: string;
  /** Rótulo da etapa do wizard (ex.: "1 · Cliente"). */
  groupLabel?: string;
  /** Exige clique no elemento destacado — oculta "Próximo". */
  requiresInteraction?: boolean;
  /** Só avança via código (ex.: após salvar) — oculta "Próximo". */
  advanceProgrammaticallyOnly?: boolean;
  /** No último campo, exibe botão para ir ao próximo passo do tour (sem exigir conclusão no checklist). */
  advanceToNextStepOnFinish?: boolean;
}

export const ONBOARDING_STEP_TARGETS: Record<string, OnboardingStepTarget[]> = {
  BRANDING: [
    {
      id: 'BRANDING__LOGO',
      hint: 'Clique na área tracejada para selecionar o logo do estabelecimento. Use PNG, JPG ou WebP com até 2 MB.',
    },
    {
      id: 'BRANDING__SLOGAN',
      hint: 'Digite um slogan curto que represente seu estabelecimento. Ele aparecerá na vitrine online.',
    },
    {
      id: 'BRANDING__PRIMARY_COLOR',
      hint: 'Escolha a cor primária da marca. Use o seletor de cor ou informe o código hexadecimal.',
    },
    {
      id: 'BRANDING__SECONDARY_COLOR',
      hint: 'Escolha a cor secundária para complementar a identidade visual do estabelecimento.',
    },
    {
      id: 'BRANDING__SAVE',
      hint: 'Clique em Salvar Personalização para aplicar slogan e cores. O logo é salvo ao confirmar o upload.',
    },
  ],
  BUSINESS_HOURS: [
    {
      id: 'BUSINESS_HOURS__REGULAR',
      hint: 'Marque os dias em que o estabelecimento funciona e defina horário de abertura e fechamento.',
    },
    {
      id: 'BUSINESS_HOURS__SAVE',
      hint: 'Clique em Salvar Alterações para registrar os horários de funcionamento.',
    },
  ],
  SERVICES: [
    {
      id: 'SERVICES__CREATE',
      hint: 'Clique em Novo serviço para abrir o formulário de cadastro.',
      requiresInteraction: true,
    },
    {
      id: 'SERVICES__NAME',
      hint: 'Informe o nome do serviço, como aparecerá para os clientes na vitrine.',
    },
    {
      id: 'SERVICES__DESCRIPTION',
      hint: 'Descreva brevemente o serviço. Este campo é opcional, mas ajuda na vitrine.',
    },
    {
      id: 'SERVICES__DURATION',
      hint: 'Defina a duração total do serviço em minutos.',
    },
    {
      id: 'SERVICES__PRICE',
      hint: 'Informe o preço cobrado por este serviço.',
    },
    {
      id: 'SERVICES__PROFESSIONAL_ACTIVE',
      hint: 'Opcional: informe quantos minutos o profissional fica ocupado de fato. Se vazio, usa a duração total.',
    },
    {
      id: 'SERVICES__SUBMIT',
      hint: 'Clique em Criar serviço para salvar o cadastro.',
    },
  ],
  ACCESS_CONTROL: [
    {
      id: 'ACCESS_CONTROL__NAME',
      hint: 'Informe um nome para o grupo de acesso do profissional, como "Profissional" ou "Recepção".',
    },
    {
      id: 'ACCESS_CONTROL__DESCRIPTION',
      hint: 'Opcional: descreva brevemente para que serve este grupo de permissões.',
    },
    {
      id: 'ACCESS_CONTROL__PERMISSIONS',
      hint: 'Marque as permissões que os profissionais deste grupo terão no sistema.',
    },
    {
      id: 'ACCESS_CONTROL__SUBMIT',
      hint: 'Clique em Criar grupo para salvar o grupo de acesso.',
    },
  ],
  TEAM: [
    {
      id: 'TEAM__ENABLE_AS_PROFESSIONAL',
      groupLabel: 'Equipe',
      hint: 'Se você também atender clientes, use Quero atuar como profissional para aparecer na agenda. Opcional se você só gerencia a equipe.',
    },
    {
      id: 'TEAM__INVITE',
      hint: 'Clique em Convidar Profissional para abrir o formulário de convite.',
      requiresInteraction: true,
    },
    {
      id: 'TEAM__EMAIL',
      hint: 'Informe o e-mail da pessoa que você deseja convidar para a equipe.',
    },
    {
      id: 'TEAM__GROUP',
      hint: 'Selecione o grupo de acesso que você criou no passo anterior.',
    },
    {
      id: 'TEAM__SUBMIT',
      hint: 'Clique em Enviar Convite para concluir o cadastro do profissional.',
    },
  ],
  SERVICE_ASSIGNMENT: SERVICE_ASSIGNMENT_ONBOARDING_STEP_TARGETS,
  SUBSCRIPTION: [
    {
      id: 'SUBSCRIPTION__PLANS',
      hint: 'Escolha um plano disponível e conclua a assinatura para ativar todos os recursos.',
    },
  ],
  FIRST_COMPLETED_APPOINTMENT: APPOINTMENT_ONBOARDING_STEP_TARGETS,
};

export function getStepTargets(stepId: string): OnboardingStepTarget[] {
  return ONBOARDING_STEP_TARGETS[stepId] ?? [];
}
